/**
 * Domain switcher functionality
 * Handles switching between .nl and .be domains while preserving the current page path
 * Checks if the target page exists before redirecting to prevent 404 errors
 */

const getDomainSwitcherData = () =>
    typeof domainSwitcherData !== 'undefined' ? domainSwitcherData : null;

class DomainSwitcher {
    constructor() {
        const config = getDomainSwitcherData();
        this.config = config;
        this.nlSiteUrl = config?.nlSiteUrl;
        this.beSiteUrl = config?.beSiteUrl;
        this.checkedUrls = new Map(); // Cache voor checked URLs

        if (!this.nlSiteUrl || !this.beSiteUrl) {
            console.warn('Domain switcher: Site URLs not available');
            return;
        }

        this.preloadPageExistence();
    }

    /**
     * Get the current page path (everything after the domain)
     */
    getCurrentPath() {
        return window.location.pathname + window.location.search + window.location.hash;
    }

    /**
     * Preload page existence checks for both domains
     */
    async preloadPageExistence() {
        const countryLinks = document.querySelectorAll('.c-country-picker a, .js-language-modal .l-product-info__buttons a');
        const currentPath = this.getCurrentPath();

        // Disable links één keer aan het begin
        this.addDisabledToLinks();

        // Verzamel alle unique target URLs
        const uniqueChecks = new Map();

        for (const link of countryLinks) {
            const originalHref = link.getAttribute('href');

            try {
                const originalUrl = new URL(originalHref);
                const targetUrl = new URL(originalUrl.origin + currentPath);

                originalUrl.searchParams.forEach((value, key) => {
                    targetUrl.searchParams.set(key, value);
                });

                const targetUrlString = targetUrl.toString();

                // Groepeer links per unique target URL
                if (!uniqueChecks.has(targetUrlString)) {
                    uniqueChecks.set(targetUrlString, []);
                }
                uniqueChecks.get(targetUrlString).push({ link, originalHref });

            } catch (error) {
                console.warn('Error preparing link check for:', originalHref, error);
                link.href = originalHref;
            }
        }

        // Voer alle checks parallel uit
        const checkPromises = Array.from(uniqueChecks.keys()).map(async (targetUrl) => {
            const exists = await this.checkPageExistsViaProxy(targetUrl);
            return { targetUrl, exists };
        });

        // Wacht op alle resultaten
        const results = await Promise.all(checkPromises);

        // Update alle links op basis van resultaten
        results.forEach(({ targetUrl, exists }) => {
            const linksData = uniqueChecks.get(targetUrl);
            linksData.forEach(({ link, originalHref }) => {
                if (exists) {
                    link.href = targetUrl;
                } else {
                    link.href = originalHref;
                }
            });
        });

        // Enable links één keer aan het einde
        this.removeDisabledFromLinks();
    }


    /**
     * Check page existence via a WordPress AJAX endpoint
     */
    async checkPageExistsViaProxy(targetUrl) {
        // Check cache eerst
        if (this.checkedUrls.has(targetUrl)) {
            return this.checkedUrls.get(targetUrl);
        }

        try {
            const response = await fetch(this.config?.ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'check_page_exists',
                    target_url: targetUrl,
                    nonce: this.config?.nonce || ''
                })
            });

            const result = await response.json();
            const exists = result.success && result.data.exists;

            // Cache het resultaat
            this.checkedUrls.set(targetUrl, exists);

            return exists;
        } catch (error) {
            console.warn('Error checking page via proxy:', error);
            return false;
        }
    }

    addDisabledToLinks() {
        const countryLinks = document.querySelectorAll('.c-country-picker a, .js-language-modal .l-product-info__buttons a');
        countryLinks.forEach(link => {
            link.classList.add('disabled');
        });
    }

    removeDisabledFromLinks() {
        const countryLinks = document.querySelectorAll('.c-country-picker a, .js-language-modal .l-product-info__buttons a');
        countryLinks.forEach(link => {
            link.classList.remove('disabled');
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (!getDomainSwitcherData()) {
        return;
    }

    new DomainSwitcher();
});